import {
    requestDefaultBrandPromo,
    requestPromoForCode,
    calculatePromoRewards
} from 'capi';
import { List as IList, Map as IMap, fromJS } from 'immutable';
import {
    put,
    call,
    select,
    takeLatest,
    all
} from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import { getRequestPromoCode } from '../app/bootstrap';
import { orderSerializer } from '../payment/capiOrderV4ItemSerializer';
import { getNewItemFormValues, newItemIsPlastic, newItemDenomination } from '../item/newItemForm';
import {
    getPaidDigitalItems,
    getShippingGroups,
    getActiveItems
} from '../item/itemSelectors';
import { precartOrderMetaData, cartOrderMetaData } from '../payment/paymentSelectors';
import { getCapiClient } from '../session/sessionSelectors';
import { removeQueryParams } from '../routing/routing';

import {
    getActivePromo,
    getActivePromoCode,
    getRejectedPromo
} from './promoSelectors';
import {
    GET_INITIAL_PROMO,
    GET_DEFAULT_PROMO,
    GET_PROMO_FOR_CODE,
    APPLY_ACTIVE_PROMO,
    APPLY_ACTIVE_PROMO_PRE_CART,
    INVALID_PROMO_ERROR,
    EXPIRED_PROMO_ERROR,
    SOLD_OUT_PROMO_ERROR,
    PROMO_VALID_SUCCESS,
    SET_REJECTED_PROMO,
    RESET_PROMO,
    setPromo,
    setPromoError,
    setPromoRewards,
    setRejectedPromo,
    finishPromoLoad,
    setPromoStatus,
    clearPromoStatus,
    clearRejectedPromos,
    clearActivePromo,
    setRewardsRequestActive,
    setRewardsRequestFailure,
    finishedGettingPromoRewards
} from './promoModule';


function* getDefaultPromo() {
    const capiClient = yield select(getCapiClient);
    try {
        return yield call(requestDefaultBrandPromo, capiClient);
    } catch (error) {
        yield put(setPromoError('default', error));
        return null;
    }
}

function* setDefaultPromo(defaultPromoData) {
    const rejectedPromoCode = yield select(getRejectedPromo);
    if (defaultPromoData && defaultPromoData.promoCode !== rejectedPromoCode) {
        yield put(setPromo(defaultPromoData));
    }
}


export function* fetchDefaultPromo() {
    const defaultPromoData = yield call(getDefaultPromo);
    yield call(setDefaultPromo, defaultPromoData);
}


function* getPromoForCode(promoCode) {
    const capiClient = yield select(getCapiClient);
    try {
        return yield call(
            requestPromoForCode,
            capiClient,
            promoCode
        );
    } catch (error) {
        yield put(setPromoError(promoCode, error));
        return {};
    }
}


const isSuccessStatus = status => status === PROMO_VALID_SUCCESS;


const getPromoStatus = (promoData, applySoldOutPromo) => {
    if (!promoData || !promoData.promoCode) {
        return INVALID_PROMO_ERROR;
    }

    if (!promoData.active) {
        return EXPIRED_PROMO_ERROR;
    }

    if (promoData.soldOut && !applySoldOutPromo) {
        return SOLD_OUT_PROMO_ERROR;
    }

    return PROMO_VALID_SUCCESS;
};


export function* fetchPromoForCode(action) {
    const { promoCode, applySoldOutPromo } = action;
    const promoData = yield call(getPromoForCode, promoCode);
    const statusType = getPromoStatus(promoData, applySoldOutPromo);

    if (isSuccessStatus(statusType)) {
        yield put(setPromo(promoData));
    }
    yield put(setPromoStatus({ statusType, promoCode }));

    return statusType;
}


function* capiV4ApplyPromoRewards(promoCode, metaData, digitalItems, shippingGroups) {
    
    const payment = IMap({
        senderEmail: `${uuid()}@dummy.email`,
        firstName: 'DUMMYNAME'
    });
    const orderData = yield call(orderSerializer, payment, metaData, digitalItems, shippingGroups);

    const capiClient = yield select(getCapiClient);
    try {
        yield put(setRewardsRequestActive());

        const rewards = yield call(
            calculatePromoRewards,
            capiClient,
            promoCode,
            orderData
        );

        if (rewards) {
            yield put(setPromoRewards(fromJS(rewards)));
        } else {
            yield put(setPromoRewards(IMap()));
        }
    } catch (error) {
        yield put(setPromoError(promoCode, error));
        yield put(setRewardsRequestFailure());
    }
    yield put(finishedGettingPromoRewards());
}


export function* applyPromoRewardsPreCart({ includeAllItems, skipDigital }) {
    const newItem = yield select(getNewItemFormValues);
    const promo = yield select(getActivePromo);
    const isPlastic = yield select(newItemIsPlastic);

    if (promo && promo.size && newItem && (!skipDigital || (skipDigital && isPlastic))) {
        yield call(applyDigitalAndPhysicalPromoRewards, promo, includeAllItems, isPlastic, newItem);
    } else {
        yield put(finishedGettingPromoRewards());
    }
}

export function* applyDigitalAndPhysicalPromoRewards(promo, includeAllItems, isPlastic, newItem) {
    const promoCode = promo.get('promoCode');
    const metaData = yield select(precartOrderMetaData);
    let digitalItems = includeAllItems ? yield select(getPaidDigitalItems) : IList();
    let shippingGroups = includeAllItems ? yield select(getShippingGroups) : IList();
    const newItemSumFaceValue = yield select(newItemDenomination);
    if (isPlastic) {
        const newItemShippingGroup = fromJS({
            address: {
                city: newItem.get('city', 'Portland'),
                countryCode: newItem.get('countryCode', 'US'),
                fullName: newItem.get('fullName', 'DUMMYNAME'),
                postalCode: newItem.get('postalCode', '04101'),
                state: newItem.get('state', 'ME'),
                street1: newItem.get('street1', '25 Pearl Street')
            },
            items: [newItem],
            shippingCost: 0,
            shippingMethod: { token: 'DUMMYTOKEN' },
            sumFaceValue: newItemSumFaceValue
        });
        shippingGroups = shippingGroups.push(newItemShippingGroup);
    } else {
        digitalItems = digitalItems.push(newItem);
    }
    yield call(capiV4ApplyPromoRewards, promoCode, metaData, digitalItems, shippingGroups);
}

export function* applyPromoRewards() {
    const activeItems = yield select(getActiveItems);
    const promo = yield select(getActivePromo);

    
    if (promo && promo.size && activeItems && activeItems.size) {
        const promoCode = promo.get('promoCode');
        const metaData = yield select(cartOrderMetaData);
        const digitalItems = yield select(getPaidDigitalItems);
        const shippingGroups = yield select(getShippingGroups);
        yield call(capiV4ApplyPromoRewards, promoCode, metaData, digitalItems, shippingGroups);
    } else {
        yield put(finishedGettingPromoRewards());
    }
}


function* handleDefaultPromo() {
    yield call(fetchDefaultPromo);
    yield call(applyPromoRewards);
    yield put(finishPromoLoad());
}


function* handleNonDefaultPromo(action) {
    const statusType = yield call(fetchPromoForCode, action);
    if (isSuccessStatus(statusType)) {
        yield call(applyPromoRewards);
        
        yield call(removeQueryParams, ['promoCode']);
    }
    yield put(finishPromoLoad());
}


function* checkStatusAndSetPromo(promoCode, promoData, applySoldOutPromo) {
    let success = false;
    if (promoData) {
        const statusType = yield call(getPromoStatus, promoData, applySoldOutPromo);
        success = isSuccessStatus(statusType);
    }

    if (success) {
        yield put(setPromo(promoData));
    } else {
        yield put(setRejectedPromo(promoCode));
    }

    return success;
}


function* getAllPromos(requestPromoCode, storedPromoCode) {
    const fetchPromoActions = { default: call(getDefaultPromo) };
    if (requestPromoCode) {
        fetchPromoActions.request = call(getPromoForCode, requestPromoCode);
    }
    if (storedPromoCode) {
        fetchPromoActions.stored = call(getPromoForCode, storedPromoCode);
    }
    return yield all(fetchPromoActions);
}


function* initialGetPromo() {
    const requestPromoCode = yield call(getRequestPromoCode);
    const storedPromoCode = yield select(getActivePromoCode);

    const allPromoData = yield call(getAllPromos, requestPromoCode, storedPromoCode);

    
    let validRequestPromo = false;
    if (requestPromoCode) {
        validRequestPromo = yield call(checkStatusAndSetPromo, requestPromoCode, allPromoData.request, true);
    }

    let validStoredPromo = false;
    
    if (storedPromoCode && !validRequestPromo) {
        validStoredPromo = yield call(checkStatusAndSetPromo, storedPromoCode, allPromoData.stored, false);
    }

    
    if (allPromoData.default && !validRequestPromo && !validStoredPromo) {
        yield call(setDefaultPromo, allPromoData.default);
    }

    
    yield call(applyPromoRewards);
    yield put(finishPromoLoad());
}


function* removeRequestPromo() {
    yield call(removeQueryParams, ['promoCode']);
}


function* handlePromoReset() {
    yield call(removeRequestPromo);
    yield put(clearPromoStatus());
    yield put(clearActivePromo());
    yield call(handleDefaultPromo);
    yield put(clearRejectedPromos());
}


export function* promoSagasWatcher() {
    yield takeLatest(GET_INITIAL_PROMO, initialGetPromo);
    yield takeLatest(GET_DEFAULT_PROMO, handleDefaultPromo);
    yield takeLatest(GET_PROMO_FOR_CODE, handleNonDefaultPromo);
    yield takeLatest(APPLY_ACTIVE_PROMO, applyPromoRewards);
    yield takeLatest(APPLY_ACTIVE_PROMO_PRE_CART, applyPromoRewardsPreCart);
    yield takeLatest(SET_REJECTED_PROMO, removeRequestPromo);
    yield takeLatest(RESET_PROMO, handlePromoReset);
}
